<template>
	<div class="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" @click.self="$emit('close')">
		<div class="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto" @click.stop>
			<!-- Header -->
			<div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
				<h5 class="text-xl font-semibold text-gray-900 tracking-wide">Bulk Create Entries</h5>
				<button @click="$emit('close')" class="text-gray-400 hover:text-gray-600 transition-colors">
					<svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
					</svg>
				</button>
			</div>

			<!-- Content -->
			<div class="px-6 py-6">
				<!-- Count Display -->
				<div v-if="unlinkedLots.length > 0" class="mb-6 text-sm text-gray-600">
					<span>{{ unlinkedLots.length }} {{ unlinkedLots.length === 1 ? 'entry' : 'entries' }} will be created.</span>
				</div>

				<!-- Form -->
				<div class="space-y-4">
					<SectionTypeFilter v-model="sectionType" />
					<StageFilter v-model="stageId" :available-stages="availableStages" />
					<StatusFilter v-model="status" />
					<EnableDisableToggle v-model="enabled" />
					<PublishScheduler v-model="publishData" :show-helper-text="true" />
				</div>

				<!-- Progress Bar (shown during processing) -->
				<div v-if="isProcessing" class="mt-6">
					<div class="mb-2 flex justify-between text-sm text-gray-600">
						<span>Processing...</span>
						<span>{{ Math.round(progress * 100) }}%</span>
					</div>
					<div class="w-full bg-gray-200 rounded-full h-2.5">
						<div
							class="bg-black h-2.5 rounded-full transition-all duration-300"
							:style="{ width: (progress * 100) + '%' }"
						></div>
					</div>
					<div class="mt-2 text-sm text-gray-600">
						{{ processedItems }} / {{ totalItems }} entries processed
					</div>
				</div>
			</div>

			<!-- Footer Actions -->
			<div class="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
				<button
					@click="$emit('close')"
					class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
					:disabled="isProcessing"
				>
					Cancel
				</button>
				<button
					@click="handleCreateEntries"
					:disabled="isCreateDisabled"
					class="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
				>
					{{ isProcessing ? 'Creating...' : 'Create entries' }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import EnableDisableToggle from './edit/EnableDisableToggle.vue'
import PublishScheduler from './edit/PublishScheduler.vue'
import SectionTypeFilter from './edit/SectionTypeFilter.vue'
import StageFilter from './edit/StageFilter.vue'
import StatusFilter from './edit/StatusFilter.vue'

export default {
	name: 'BulkLotEntryPopup',
	components: {
		EnableDisableToggle,
		PublishScheduler,
		SectionTypeFilter,
		StageFilter,
		StatusFilter
	},
	props: {
		availableStages: {
			type: Array,
			required: true
		},
		unlinkedLots: {
			type: Array,
			required: true
		}
	},
	emits: ['close', 'completed'],
	data() {
		return {
			stageId: '',
			status: 'available',
			sectionType: 'section',
			enabled: true,
			publishData: {
				publishOption: 'now',
				postDate: ''
			},
			isProcessing: false,
			progress: 0,
			jobId: null,
			pollInterval: null
		}
	},
	computed: {
		totalItems() {
			return this.unlinkedLots.length
		},
		processedItems() {
			return Math.round(this.progress * this.totalItems)
		},
		isCreateDisabled() {
			if (this.isProcessing) return true
			if (this.unlinkedLots.length === 0) return true
			// If scheduling, must have a post date
			if (this.publishData.publishOption === 'schedule' && !this.publishData.postDate) {
				return true
			}
			return false
		}
	},
	methods: {
		async handleCreateEntries() {
			this.isProcessing = true
			this.progress = 0
			
			try {
				const csrfToken = this.getCsrfToken()
				
				const requestBody = {
					lots: this.unlinkedLots,
					sectionType: this.sectionType,
					stageId: this.stageId,
					sectionStatus: this.status,
					enabled: this.enabled
				}
				
				if (this.publishData.publishOption === 'schedule') {
					requestBody.postDate = this.publishData.postDate
				} else if (this.publishData.publishOption === 'now') {
					const today = new Date()
					today.setHours(0, 0, 0, 0)
					requestBody.postDate = today.toISOString()
				}
				
				const response = await fetch('/api/site-plan/queue-bulk-create-entries', {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-CSRF-Token': csrfToken
					},
					body: JSON.stringify(requestBody)
				})
				
				const data = await response.json()
				
				if (data.success && data.jobId) {
					this.jobId = data.jobId
					this.pollProgress()
				} else {
					alert('Failed: ' + (data.error || 'Unknown error'))
					this.isProcessing = false
				}
			} catch (error) {
				alert('Error: ' + error.message)
				this.isProcessing = false
			}
		},

		pollProgress() {
			this.pollInterval = setInterval(async () => {
				try {
					const response = await fetch(`/api/site-plan/bulk-create-progress?jobId=${this.jobId}`)
					const data = await response.json()
					
					if (data.success) {
						this.progress = data.progress
						
						if (data.progress >= 1.0 || data.status === 'complete') {
							clearInterval(this.pollInterval)
							this.isProcessing = false
							this.$emit('completed')
							setTimeout(() => this.$emit('close'), 1000)
						}
					}
				} catch (error) {
					console.error('Error polling progress:', error)
				}
			}, 1000)
		},
		
		getCsrfToken() {
			const metaToken = document.querySelector('meta[name="csrf-token"]')
			if (metaToken) {
				return metaToken.getAttribute('content')
			}
			if (typeof window.Craft !== 'undefined' && window.Craft.csrfTokenValue) {
				return window.Craft.csrfTokenValue
			}
			throw new Error('CSRF token not found')
		}
	},
	beforeUnmount() {
		if (this.pollInterval) {
			clearInterval(this.pollInterval)
		}
	}
}
</script>